import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, UserCheck, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerAutocomplete({ onSelect, className = '' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const timeout = useRef(null);
  const wrapperRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/customers/search`, { params: { q }, withCredentials: true });
      setResults(data);
      setOpen(data.length > 0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => search(val), 280);
  };

  const handleSelect = (customer) => {
    setSelected(customer);
    setQuery(customer.name);
    setOpen(false);
    setResults([]);
    onSelect(customer);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8A9BA8]" />
        <input
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Buscar cliente guardado..."
          className={`w-full border rounded-lg pl-8 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30 transition-colors ${selected ? 'border-[#4A6D7C] bg-[#4A6D7C]/5' : 'border-[#E5E0D8]'}`}
        />
        {selected
          ? <UserCheck className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#4A6D7C]" />
          : query && <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C5BFB8] hover:text-[#8A9BA8]"><X className="w-3.5 h-3.5" /></button>
        }
      </div>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E0D8] rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="px-4 py-3 text-xs text-[#8A9BA8]">Buscando...</div>
          ) : (
            results.map(c => (
              <button key={c.id} type="button" onMouseDown={() => handleSelect(c)}
                className="w-full text-left px-4 py-2.5 hover:bg-[#F5F2ED] transition-colors flex items-center justify-between gap-3 border-b border-[#F5F2ED] last:border-0">
                <div>
                  <p className="text-sm font-semibold text-[#1A2B3C]">{c.name}</p>
                  <p className="text-xs text-[#8A9BA8]">{[c.phone, c.email].filter(Boolean).join(' · ') || 'Sin contacto'}</p>
                </div>
                <UserCheck className="w-4 h-4 text-[#4A6D7C] shrink-0" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
